/**
 * React Query hooks for CRM AI features.
 *
 * AI calls are paid one-shot operations — modeled as mutations rather
 * than queries so callers fire them deliberately and we don't
 * accidentally re-trigger on re-renders. The hook returns a unified
 * `loading` boolean across all in-flight mutations so consumers can
 * disable buttons during any AI call.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import api, {
  CrmAISummaryResult,
  CrmLeadScoreResult,
  CrmSemanticSearchResult,
  CrmWinLikelihoodResult,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

export function useCrmAI() {
  const queryClient = useQueryClient();

  const scoreLeadMutation = useMutation({
    mutationFn: (contactId: string) => api.scoreLead(contactId),
    onSuccess: (result) => {
      toast({ title: 'Lead Scored', description: `Score: ${result.score}/100` });
      // The score is persisted on the contact; refresh the contact cache.
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.contacts.all() });
    },
    onError: () =>
      toast({
        title: 'Error',
        description: 'Failed to compute lead score',
        variant: 'destructive',
      }),
  });

  const summaryMutation = useMutation({
    mutationFn: ({
      entityType,
      entityId,
    }: {
      entityType: 'contact' | 'company';
      entityId: string;
    }) => api.generateAISummary(entityType, entityId),
    onSuccess: (_result, vars) => {
      toast({ title: 'Summary Generated', description: 'AI summary has been updated' });
      const keys =
        vars.entityType === 'contact'
          ? queryKeys.crm.contacts.all()
          : queryKeys.crm.companies.all();
      queryClient.invalidateQueries({ queryKey: keys });
    },
    onError: () =>
      toast({ title: 'Error', description: 'Failed to generate summary', variant: 'destructive' }),
  });

  const winLikelihoodMutation = useMutation({
    mutationFn: (dealId: string) => api.computeWinLikelihood(dealId),
    onSuccess: (result) => {
      toast({ title: 'Win Prediction', description: `Likelihood: ${result.likelihood}%` });
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.deals.all() });
    },
    onError: () =>
      toast({
        title: 'Error',
        description: 'Failed to compute win likelihood',
        variant: 'destructive',
      }),
  });

  const searchSemanticMutation = useMutation({
    mutationFn: ({ entityType, query }: { entityType: 'contact' | 'company'; query: string }) =>
      api.semanticSearch(entityType, query).then((r) => r.results),
    onError: () =>
      toast({ title: 'Error', description: 'Semantic search failed', variant: 'destructive' }),
  });

  const findSimilarMutation = useMutation({
    mutationFn: ({
      entityType,
      entityId,
    }: {
      entityType: 'contact' | 'company';
      entityId: string;
    }) => api.findSimilarEntities(entityType, entityId).then((r) => r.results),
    onError: () =>
      toast({
        title: 'Error',
        description: 'Failed to find similar entities',
        variant: 'destructive',
      }),
  });

  const embeddingMutation = useMutation({
    mutationFn: ({
      entityType,
      entityId,
    }: {
      entityType: 'contact' | 'company';
      entityId: string;
    }) => api.generateCrmEmbedding(entityType, entityId),
  });

  const loading =
    scoreLeadMutation.isPending ||
    summaryMutation.isPending ||
    winLikelihoodMutation.isPending ||
    searchSemanticMutation.isPending ||
    findSimilarMutation.isPending ||
    embeddingMutation.isPending;

  return {
    loading,
    scoreLead: async (contactId: string): Promise<CrmLeadScoreResult | null> => {
      try {
        return await scoreLeadMutation.mutateAsync(contactId);
      } catch {
        return null;
      }
    },
    generateSummary: async (
      entityType: 'contact' | 'company',
      entityId: string,
    ): Promise<CrmAISummaryResult | null> => {
      try {
        return await summaryMutation.mutateAsync({ entityType, entityId });
      } catch {
        return null;
      }
    },
    predictWinLikelihood: async (dealId: string): Promise<CrmWinLikelihoodResult | null> => {
      try {
        return await winLikelihoodMutation.mutateAsync(dealId);
      } catch {
        return null;
      }
    },
    searchSemantic: async (
      entityType: 'contact' | 'company',
      query: string,
    ): Promise<CrmSemanticSearchResult[]> => {
      try {
        return await searchSemanticMutation.mutateAsync({ entityType, query });
      } catch {
        return [];
      }
    },
    findSimilar: async (
      entityType: 'contact' | 'company',
      entityId: string,
    ): Promise<CrmSemanticSearchResult[]> => {
      try {
        return await findSimilarMutation.mutateAsync({ entityType, entityId });
      } catch {
        return [];
      }
    },
    generateEmbedding: async (
      entityType: 'contact' | 'company',
      entityId: string,
    ): Promise<boolean> => {
      try {
        await embeddingMutation.mutateAsync({ entityType, entityId });
        return true;
      } catch {
        return false;
      }
    },
  };
}
